import { initReactI18next } from "react-i18next";
import i18n from "i18next";
import Backend from "i18next-http-backend";
import LanguageDetector from "i18next-browser-languagedetector";
import ICU from "i18next-icu";
import { createLogger } from "@/utils/debug";

const log = createLogger("i18n");

/**
 * ICU parse/format failure fallback. i18next-icu's DEFAULT handler returns
 * the raw resource string, so a message-format error (e.g. a service payload
 * missing its `{username}` variable) would render the literal pattern —
 * braces and all — as chat content. Strip the placeholders instead and log:
 * a degraded-but-clean string beats "{username} was removed from the chat".
 *
 * Exported so serviceMessageI18n.test.ts exercises the REAL handler.
 */
export const icuParseErrorHandler = (
  err: Error,
  key: string,
  res: unknown,
): unknown => {
  log.warn(`ICU format failed for "${key}": ${err.message}`);
  // i18next-icu's d.ts types `res` as string, but the plugin passes the RAW
  // resource that failed to format — and with an i18nFormat plugin installed,
  // i18next skips its own returnObjects handling, so array/object resources
  // (installAppIOS.benefits, subscription.faqItems) always land here. Return
  // them unchanged like the plugin's default handler does, so caller-side
  // Array.isArray guards keep working; brace-stripping only applies to
  // strings. Calling .replace on an array here crashed the root boundary at
  // boot (staging, 2026-08-24).
  if (typeof res !== "string") {
    return res;
  }
  // Strip innermost braces REPEATEDLY — a single pass leaves the outer braces
  // of nested ICU plural/select patterns behind, which is the exact
  // raw-braces-on-screen failure this handler exists to prevent. Bounded by
  // the nesting depth of the resource string.
  let out = res;
  while (/\{[^{}]*\}/.test(out)) {
    out = out.replace(/\{[^{}]*\}/g, "");
  }
  return out.replace(/\s{2,}/g, " ").trim();
};

i18n
  // detect user language
  // learn more: https://github.com/i18next/i18next-browser-languageDetector
  .use(LanguageDetector)
  .use(Backend)
  .use(ICU)
  // Pass the i18n instance to react-i18next.
  .use(initReactI18next)
  // Init i18next
  .init({
    returnNull: false,
    fallbackLng: "en",
    debug: true,
    interpolation: {
      escapeValue: false, // Not needed for react as it escapes by default
    },
    i18nFormat: {
      parseErrorHandler: icuParseErrorHandler,
    },
    ns: ["common"],
    defaultNS: "common",
    backend: {
      loadPath: "/lang/{{lng}}/{{ns}}.json",
      defaultNS: "common",
    },
  });

export default i18n;
